<template>
  <feature-toggle toggle="enableAOExtracts">
    <template v-if="workpackage.aoExtracts && workpackage.aoExtracts.length > 0">
      <v-row no-gutters class="text-row pl-4">
        <v-col class="pb-0 pt-2 ao-extracts">
          <span>{{ $t('workPackages.aoExtracts') }}:</span>
        </v-col>
      </v-row>

      <v-row
        v-for="extract in workpackage.aoExtracts"
        :key="extract.auditCorrelationId"
        no-gutters
        class="text-row pl-4"
      >
        <v-col class="pb-0 pt-2 ao-extracts">
          <strong>{{ getAOExtractsByKey[extract.auditCorrelationId].displayName }}</strong>
        </v-col>
      </v-row>

      <feature-toggle toggle="usePeriodIdsInAOExtracts">
        <v-row no-gutters class="text-row pl-4">
          <v-col class="pb-0 pt-2 ao-extracts">
            <span>{{ $t('workPackages.associatedPeriodIds') }}:</span>
          </v-col>
        </v-row>

        <v-row
          v-for="periodId in workpackageAssociatedPeriodIds"
          :key="periodId"
          no-gutters
          class="text-row pl-4"
        >
          <v-col class="pb-0 pt-2 ao-extracts">
            <strong>{{ periodId }}</strong>
          </v-col>
        </v-row>
      </feature-toggle>

      <v-row v-if="workpackage.missingProductsInAOExtracts" no-gutters class="text-row pl-4">
        <v-col class="pb-0 pt-2">
          <v-icon color="orange darken-2">warning</v-icon>
          {{
            $t('workPackages.missingProductsInAOExtracts', {
              count: workpackage.missingProductsInAOExtracts.length,
            })
          }}

          <v-btn class="ml-2" text outlined small @click="isMissingProductsDialogOpen = true">
            {{ $t('actions.seeAll') }}
          </v-btn>

          <info-dialog
            :is-open="isMissingProductsDialogOpen"
            :ok-btn-text="$t('actions.close')"
            @onOk="isMissingProductsDialogOpen = false"
          >
            <template v-slot:header>
              {{
                $t('workPackages.missingProductsInAOExtracts', {
                  count: workpackage.missingProductsInAOExtracts.length,
                })
              }}
            </template>
            <template v-slot:body>
              <v-expansion-panels accordion>
                <v-expansion-panel v-for="tsgKey in storeGroupsWithMissingProducts" :key="tsgKey">
                  <v-expansion-panel-header>
                    {{ storegroupKeyNameMap[tsgKey] }} -
                    {{
                      $t('workPackages.missingProductsInStoreGroup', {
                        count: missingProductsInAOExtractsByTSGKey[tsgKey].length,
                      })
                    }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    {{ missingProductsInAOExtractsByTSGKey[tsgKey].join(', ') }}
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </template>
          </info-dialog>
        </v-col>
      </v-row>
    </template>
  </feature-toggle>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { isEmpty } from 'lodash';
import { enableAOExtracts, usePeriodIdsInAOExtracts } from '@enums/feature-flags';
import featureFlagsMixin from '../../../mixins/featureFlags';

export default {
  name: 'WorkpackageAoExtracts',

  mixins: [featureFlagsMixin],

  props: {
    workpackage: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      enableAOExtracts,
      usePeriodIdsInAOExtracts,
      isMissingProductsDialogOpen: false,
    };
  },

  computed: {
    ...mapState('storeGroupRelationships', ['storegroupKeyNameMap']),
    ...mapGetters('workpackages', ['getAOExtractsByKey']),

    workpackageAssociatedPeriodIds() {
      const periodsIds = new Set();
      this.workpackage.aoExtracts.forEach(extract => {
        this.getAOExtractsByKey[extract.auditCorrelationId].periodIds.map(periodId =>
          periodsIds.add(periodId)
        );
      });
      return Array.from(periodsIds);
    },

    missingProductsInAOExtractsByTSGKey() {
      if (isEmpty(this.workpackage.missingProductsInAOExtracts)) {
        return {};
      }
      return this.workpackage.missingProductsInAOExtracts.reduce((acc, product) => {
        const tsgKey = product.toolStoreGroupKey;
        if (!acc[tsgKey]) {
          acc[tsgKey] = [];
        }
        acc[tsgKey].push(product.productKeyDisplay);
        return acc;
      }, {});
    },

    storeGroupsWithMissingProducts() {
      return Object.keys(this.storegroupKeyNameMap).filter(
        key => this.missingProductsInAOExtractsByTSGKey[key]
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.text-row {
  font-weight: normal;
  margin-left: -1.7rem;
}

.ao-extracts {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 1.2rem;
}

::v-deep {
  .v-expansion-panel-header {
    font-size: 1.4rem;
  }
  .v-expansion-panel-content__wrap {
    padding: 0rem 2rem 1rem;
  }
}
</style>
